import { api, type Note } from '../../lib/api';
import { useSelection } from '../../lib/selection';
import { periodRange } from '../../lib/scopeKey';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * 笔记 —— 列表页 + 点进去全屏编辑。
 *
 * 与改前的差别：
 *  · **每日多篇**：列表按天分组，同一天可写多篇
 *  · **不自动保存**：只有显式点「保存」才落库；空内容禁用保存
 *  · **周/月/年看该区间的所有日笔记**（点周K柱即看当周的日记）
 *
 * 原先这里是「一个永远打开的编辑器 + 800ms 防抖自动保存」，
 * 每天只能一篇，且改一个字就发一次请求。
 */

/** 取正文首行当列表标题（每日多篇时靠它区分） */
function titleOf(n: Note): string {
  const first = n.content.trim().split('\n').find((l) => l.trim().length > 0);
  if (!first) return '（空）';
  return first.trim().length > 30 ? first.trim().slice(0, 30) + '…' : first.trim();
}

export function NoteList() {
  const { instrument, period, effectiveDate } = useSelection();
  const { from, to } = periodRange(period, effectiveDate ?? '');

  const [items, setItems] = useState<Note[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ id: number } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.notes(instrument.code, from, to);
      setItems(r.items);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [instrument.code, from, to]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => { setEditing(null); }, [instrument.code, from, to]);

  // 按天分组（周/月/年下尤其需要 —— 一屏能看到当周每天各写了什么）
  // ⚠️ 必须排在下面的提前 return 之前：条件返回后再调 hook 会触发
  // 「Rendered fewer hooks than expected」并整块崩溃。
  const groups = useMemo(() => {
    const m = new Map<string, Note[]>();
    for (const n of items) {
      const arr = m.get(n.scopeKey);
      if (arr) arr.push(n); else m.set(n.scopeKey, [n]);
    }
    return [...m.entries()].sort((a, b) => (a[0] < b[0] ? 1 : -1));
  }, [items]);

  if (editing) {
    return (
      <NoteEditor
        code={instrument.code}
        date={editing.id === -1 ? (effectiveDate ?? from) : ''}
        id={editing.id === -1 ? undefined : editing.id}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); void load(); }}
      />
    );
  }

  return (
    <section className="flex min-h-0 flex-1 flex-col">
      {/* 标题行只留「+ 新建」：Tab 名已经写着「笔记」，日期区间在上面
          筛选器里，篇数在下方逐日分组头上，三者都属重复信息 */}
      <div className="flex h-8 shrink-0 items-center gap-2 border-b px-3">
        <button
          type="button"
          onClick={() => setEditing({ id: -1 })}
          className="rounded px-2 py-0.5 text-[11px]"
          style={{ border: '1px solid var(--border)', color: 'var(--accent)' }}
        >+ 新建</button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {loading && <div className="p-3 text-[12px]" style={{ color: 'var(--text-3)' }}>加载中…</div>}
        {error && <div className="p-3 text-[12px]" style={{ color: 'var(--warn)' }}>⚠ {error}</div>}
        {/* 没有笔记时列表区留空：标题行的「+ 新建」已是唯一入口，
            再加一句提示只是重复占位 */}

        {groups.map(([date, list]) => (
          <div key={date}>
            {from !== to && (
              <div className="sticky top-0 z-10 px-3 py-1 text-[10px]"
                style={{ background: 'var(--bg-surface)', color: 'var(--text-3)' }}>
                {date} · {list.length} 篇
              </div>
            )}
            {list.map((n) => (
              <button
                key={n.id}
                type="button"
                onClick={() => setEditing({ id: n.id })}
                className="block w-full border-b px-3 py-2 text-left transition-colors hover:bg-[var(--bg-hover)]"
              >
                <div className="truncate text-[12px]" style={{ color: 'var(--text-1)' }}>
                  {titleOf(n)}
                </div>
                <div className="mt-0.5 flex items-center gap-2 text-[10px]" style={{ color: 'var(--text-3)' }}>
                  <span className="num">{n.updatedAt.slice(5, 16).replace('T', ' ')}</span>
                  {(n.tags ?? []).map((t) => (
                    <span key={t} className="rounded px-1"
                      style={{ background: 'var(--bg-elevated)', color: 'var(--text-2)' }}>{t}</span>
                  ))}
                </div>
              </button>
            ))}
          </div>
        ))}
      </div>
    </section>
  );
}

/** 全屏编辑器：显式保存，空内容禁用。 */
function NoteEditor({ code, date, id, onClose, onSaved }: {
  code: string; date: string; id?: number; onClose: () => void; onSaved: () => void;
}) {
  const [content, setContent] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const baseline = useRef('');

  useEffect(() => {
    if (id === undefined) { setContent(''); setTags([]); baseline.current = ''; setLoaded(true); return; }
    void (async () => {
      try {
        const n = await api.note(code, id);
        setContent(n.content);
        setTags(n.tags ?? []);
        baseline.current = n.content;
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setLoaded(true);
      }
    })();
  }, [code, id]);

  const dirty = content !== baseline.current;
  const empty = content.trim() === '' && tags.length === 0;

  const addTag = () => {
    const t = tagInput.trim();
    if (!t || tags.includes(t)) return;
    setTags([...tags, t]);
    setTagInput('');
  };

  const save = async () => {
    if (empty || saving) return;
    setSaving(true);
    try {
      const n = id === undefined
        ? await api.createNote(code, date, { content, tags })
        : await api.updateNote(code, id, { content, tags });
      baseline.current = n.content;
      setError(null);
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (id === undefined || saving) return;
    setSaving(true);
    try {
      await api.deleteNote(code, id);
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setSaving(false);
    }
  };

  return (
    <section className="flex min-h-0 flex-1 flex-col">
      <div className="flex h-8 shrink-0 items-center gap-2 border-b px-3">
        <button type="button" onClick={onClose}
          className="text-[12px]" style={{ color: 'var(--accent)' }}>‹ 返回</button>
        <span className="text-[11px]" style={{ color: 'var(--text-3)' }}>
          {id === undefined ? `新建 · ${date}` : '编辑笔记'}
        </span>
        <span className="ml-auto flex items-center gap-2 text-[11px]">
          {dirty && <span style={{ color: 'var(--warn)' }}>未保存</span>}
          {id !== undefined && (
            <button type="button" onClick={() => void remove()}
              className="text-[11px]" style={{ color: 'var(--text-3)' }}>删除</button>
          )}
          <button
            type="button"
            onClick={() => void save()}
            disabled={empty || saving || !dirty}
            title={empty ? '内容为空，不予保存' : undefined}
            className="rounded px-2 py-0.5 text-[11px] disabled:opacity-40"
            style={{
              border: '1px solid var(--border)',
              color: empty || !dirty ? 'var(--text-3)' : 'var(--accent)',
            }}
          >{saving ? '保存中…' : '保存'}</button>
        </span>
      </div>

      <div className="flex shrink-0 flex-wrap items-center gap-1.5 px-3 py-2">
        {tags.map((t) => (
          <button key={t} type="button" onClick={() => setTags(tags.filter((x) => x !== t))}
            className="rounded-full border px-1.5 py-0.5 text-[11px]"
            style={{ color: 'var(--text-2)' }} title="点击移除">{t} ×</button>
        ))}
        <input
          value={tagInput}
          onChange={(e) => setTagInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
          onBlur={addTag}
          placeholder="+ 标签"
          className="w-20 rounded-full border px-1.5 py-0.5 text-[11px] outline-none"
          style={{ background: 'transparent', borderColor: 'var(--border)', color: 'var(--accent)' }}
        />
      </div>

      <div className="mx-2 flex min-h-0 flex-1">
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          disabled={!loaded}
          placeholder="写下当时的判断…"
          className="min-h-[140px] w-full flex-1 resize-none rounded-[6px] border p-2.5 text-[13px] leading-[1.7] outline-none"
          style={{ background: 'var(--bg-inset)', color: 'var(--text-1)' }}
        />
      </div>

      {error && (
        <div className="shrink-0 px-3 py-1 text-[11px]" style={{ color: 'var(--warn)' }}>⚠ {error}</div>
      )}
      <div className="shrink-0 px-3 pb-2 text-[10px]" style={{ color: 'var(--text-3)' }}>
        不会自动保存 —— 点右上「保存」才写入；内容为空时不可保存。
      </div>
    </section>
  );
}